<template>
  <div id="app" class="min-h-screen_ bg-white lg:bg-app-bg relative">
    <template v-if="$store.state.authInitiated && $store.state.appInitiated">
      <div v-if="!$route.meta.hideHeader" class="print:hidden">
        <header id="top-bar" class="bg-white py-2.5 md:py-2 px-2 md:px-3 border-b fixed top-0 left-0 right-0" style="z-index:1001">
          <nav class="flex flex-col md:flex-row md:items-center md:justify-between flex-wrap text-showman mx-auto">
            <!-- Top row on mobile: Logo and Menu Button -->
            <div class="flex md:flex-grow items-center justify-between w-full md:w-auto">
              <div class="flex items-center flex-grow-0">
                <a class="hidden md:block no-underline text-2xl flex items-center" href="/">
                  <img src="https://res.cloudinary.com/showman/image/upload/c_scale,h_55,q_auto,f_auto/v1631719458/showman_logo_black.png" class="h-12" alt="Showman">
                </a>
                <a class="block md:hidden no-underline text-2xl flex items-center" href="/">
                  <img src="https://res.cloudinary.com/showman/image/upload/c_scale,h_55,q_auto,f_auto/v1671228923/Showman-Banner-Orange.png" class="h-10" alt="Showman">
                </a>
              </div>
                            <!-- Search Bar - Full width on mobile, inline on desktop -->
              <div class="flex-grow mx-3 md:mx-6 px-1 md:px-0 py-1 md:py-0 bg-orange-50 md:bg-transparent rounded-lg md:rounded-none">
                <ShowSearchBar
                  class="w-full"
                  :placeholder="'Search shows...'"
                  :events="allEvents"
                  :series="allSeries"
                  :title-key="'name'"
                  :subtitle-key="(show) => show.location_details ? `${show.location_details.city}, ${show.location_details.state}` : 'Location TBD'"
                  :type-key="() => 'show'"
                  @select="handleShowSelect"
                />
              </div>
              <!-- Mobile: Help dropdown + Menu Button -->
              <div class="flex items-center md:hidden gap-1">
                <dropdown-button ref="helpDropdownMobile">
                  <div slot="link" class="flex items-center p-2 rounded-lg text-showman hover:bg-gray-200" aria-label="Help">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div slot="dropdown" class="origin-top-right absolute right-0 mt-2 max-w-sm rounded-lg shadow-lg bg-white border border-gray-200 p-2.5 focus:outline-none"
                    style="min-width: 260px" role="dialog" aria-label="Help">
                    <p class="text-base text-gray-700 text-center">
                      Having technical trouble?
                      <br>
                      <a href="/shows/#/help" class="font-bold text-showman hover:text-orange-500 no-underline hover:underline" @click="$refs.helpDropdownMobile.close()">Click here for help.</a>
                      <br>
                      Or send an email to <span class="font-bold">Support@showman.app</span>
                    </p>
                  </div>
                </dropdown-button>
                <button @click="toggleMobileMenu" class="flex items-center px-3 py-2 border border-orange-400 rounded-lg text-orange-400 hover:text-orange-500">
                  <svg v-if="!navOpen" class="fill-current h-5 w-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>Menu</title><path d="M0 2h20v3H0V2zm0 6h20v3H0V8zm0 6h20v3H0v-3z"/></svg>
                  <svg v-else class="fill-current h-5 w-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>Close</title><path d="M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z"/></svg>
                </button>
              </div>
            </div>

            <!-- Desktop Navigation -->
            <div class="hidden md:flex">
              <div class="flex items-center">
                <router-link
                  onclick="fathom.trackGoal('PPBJ1VUV', 0);"
                  class="text-sm no-underline rounded-md py-3 px-3 mr-3"
                  :class="$route.path === '/new-show-signup' ? 'bg-orange-400 text-white cursor-default' : 'bg-blue-500 hover:bg-blue-700 text-white font-bold'"
                  to="/new-show-signup"
                  :event="$route.path === '/new-show-signup' ? '' : 'click'"
                >
                  <span>Create a show</span>
                </router-link>
                <dropdown-button ref="expolorebutton">
                  <div slot="link" class="flex items-center hover:bg-gray-200 rounded-lg p-3">
                    <div class="mr-1 bold">Shows</div>
                    <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path style="fill:orange;" d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                    <div slot="dropdown" class="max-h-screen origin-top-right absolute right-0 mt-2 w-40 rounded-lg shadow-lg bg-white border border-gray-200 divide-y divide-gray-100 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                      <router-link
                        to="/"
                        class="text-sm no-underline block px-4 py-3 bg-white"
                        :class="$route.path === '/' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:text-gray-800 hover:bg-gray-200'"
                        :event="$route.path === '/' ? '' : 'click'"
                      >
                        Upcoming Shows
                      </router-link>
                      <router-link
                        to="/sponsor-a-show"
                        class="text-sm no-underline block px-4 py-3 bg-white"
                        :class="$route.path === '/sponsor-a-show' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:text-gray-800 hover:bg-gray-200'"
                        :event="$route.path === '/sponsor-a-show' ? '' : 'click'"
                      >
                        Sponsor a Show
                      </router-link>
                      <router-link
                        to="/past-shows"
                        class="text-sm no-underline block px-4 py-3 bg-white"
                        :class="$route.path === '/past-shows' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:text-gray-800 hover:bg-gray-200'"
                        :event="$route.path === '/past-shows' ? '' : 'click'"
                      >
                        Past Shows
                      </router-link>
                    </div>
                </dropdown-button>
                <router-link
                  to="/show-series"
                  class="flex-0 no-underline rounded-lg py-3 px-3"
                  :class="$route.path === '/show-series' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-showman hover:bg-gray-200'"
                  :event="$route.path === '/show-series' ? '' : 'click'"
                >
                  Show Series
                </router-link>
                <router-link
                  to="/judgeFinder"
                  class="flex-0 no-underline rounded-lg py-3 px-3"
                  :class="$route.path === '/judgeFinder' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-showman hover:bg-gray-200'"
                  :event="$route.path === '/judgeFinder' ? '' : 'click'"
                >
                  Judges
                </router-link>
                <a
                  href="/learn.html#/"
                  class="flex-0 no-underline rounded-lg py-3 px-3 text-showman hover:bg-gray-200"
                >
                  Learn
                </a>
                <dropdown-button ref="helpDropdownDesktop">
                  <div slot="link" class="flex items-center p-3 rounded-lg text-showman hover:bg-gray-200" aria-label="Help" title="Help">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div slot="dropdown" class="origin-top-right absolute right-0 mt-2 max-w-sm rounded-lg shadow-lg bg-white border border-gray-200 p-4 focus:outline-none"
style="min-width: 380px" role="dialog" aria-label="Help">
                    <p class="text-base text-gray-700 text-center">
                      Having technical trouble? <a href="/shows/#/help" class="font-bold text-showman hover:text-orange-500 no-underline hover:underline" @click="$refs.helpDropdownDesktop.close()">Click here for help.</a><br>Or send an email to <span class="font-bold">Support@showman.app</span>
                    </p>
                  </div>
                </dropdown-button>

                <div class="border-l ml-2 pl-2">
                  <div v-show="!currentUser" class="flex items-center">
                    <router-link class="flex-0 text-showman hover:bg-gray-200 rounded-lg p-3 no-underline" to="/signin">Sign In</router-link>
                  </div>
                  <div v-if="currentUser" class="block">
                    <dropdown-button ref="menu2">
                      <div slot="link" class="flex items-center hover:bg-gray-200 rounded-lg py-3 px-3">
                        <!-- Circular Avatar with Initial -->
                        <div class="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-2">
                          {{ getUserInitial() }}
                        </div>
                        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path style="fill:orange;" d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                      <div slot="dropdown" class="max-h-screen origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white border border-gray-200 divide-y divide-gray-100 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                        <div v-if="upcomingShowLinks.length" class="border-b border-gray-400">
                          <div class="no-underline block px-4 py-1 border-b text-white bg-gray-600 cursor-pointer font-medium text-xs">Upcoming Shows</div>
                          <template v-for="(e,i) in upcomingShowLinks">
                            <router-link :key="'upcoming-' + i" :to="`/${e.event_identifier}`" class="flex items-center justify-between text-gray-700 block px-4 py-2 text-sm hover:bg-gray-200 hover:text-gray-900">{{e.name}} <span class="ml-1 text-xs font-bold whitespace-nowrap">{{formatShowDate(e.start_date)}}</span></router-link>
                          </template>
                        </div>
                        <div v-if="userData.admin_data.adminFor.length" class="border-b border-gray-400">
                          <div class="no-underline block px-4 py-1 border-b  text-white bg-gray-600 cursor-pointer font-medium text-xs">Recent Admin Links</div>
                          <template v-for="(e,i) in adminDropdownLinks">
                            <router-link :key="i" :to="{name:'admin.event', params:{show:e.event_identifier}}" class="flex items-center justify-between text-gray-700 block px-4 py-2 text-sm hover:bg-gray-200 hover:text-gray-900">{{e.name}} <span class="ml-1 text-xs font-bold">({{e.year}})</span></router-link>
                          </template>
                        </div>
                        <template v-if="currentUser.role==='ADMIN'">
                          <router-link :to="{name:'Admin'}" class="flex items-center justify-between text-gray-700 block px-4 py-2 text-sm hover:bg-gray-200 hover:text-gray-900">Showman Admin</router-link>
                        </template>
                        <a href="#" class="text-sm no-underline block px-4 py-3 text-gray-700 bg-white hover:text-gray-800 hover:bg-gray-200" @click="() => {$refs.menu2.close(); $router.push({name:'my-profile'})}">My Profile</a>
                        <div class="text-sm no-underline block px-4 py-3 text-gray-700 bg-white hover:text-gray-800 hover:bg-gray-200 cursor-pointer" @click="() => {$refs.menu2.close(); signout()}">Sign out</div>
                      </div>
                    </dropdown-button>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </header>

        <!-- Impersonation Banner (Global) -->
        <div id="impersonation-bar" v-if="$store.getters.isImpersonating" class="bg-yellow-400 border-b border-yellow-500 px-4 py-2 text-sm text-yellow-900 font-medium print:hidden fixed top-0 left-0 right-0" style="z-index: 1000; margin-top: 70px;">
          <div class="flex items-center justify-between max-w-7xl mx-auto">
            <div class="flex items-center">
              <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
              <span>
                🎭 <strong>Impersonating:</strong>
                <span v-if="$store.getters.impersonationStatus">
                  {{ $store.getters.impersonationStatus.impersonatedUser.email }}
                  <span v-if="$store.getters.impersonationStatus.impersonatedUser.first_name">
                    ({{ $store.getters.impersonationStatus.impersonatedUser.first_name }} {{ $store.getters.impersonationStatus.impersonatedUser.last_name }})
                  </span>
                </span>
              </span>
            </div>
            <button
              @click="stopImpersonation"
              class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
            >
              Stop Impersonation
            </button>
          </div>
        </div>

        <!-- Full Screen Mobile Menu -->
        <transition name="mobile-menu">
          <div v-if="navOpen" class="fixed inset-0 z-50 md:hidden" style="z-index: 999;">
            <!-- Full Screen Menu Panel -->
            <div class="fixed inset-0 bg-white flex flex-col">
              <!-- Header with Logo and Close Button -->
              <div class="flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0">
                <!-- Logo -->
                <a class="no-underline flex items-center" href="/" @click="navOpen = false">
                  <img src="https://res.cloudinary.com/showman/image/upload/c_scale,h_50,q_auto,f_auto/v1631719458/showman_logo_black.png" class="h-10" alt="Showman">
                </a>

                <!-- Close Button -->
                <button @click="navOpen = false" class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <!-- Menu Items - Keep existing content -->
              <div class="flex-1 overflow-y-auto min-h-0">
                <!-- Shows Section -->
                <div class="p-4 border-b">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">Shows</h3>
                  <div class="space-y-0">
                    <router-link
                      to="/"
                      @click="$route.path !== '/' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline"
                      :class="$route.path === '/' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:bg-gray-100'"
                      :event="$route.path === '/' ? '' : 'click'"
                    >
                      Upcoming Shows
                    </router-link>
                    <router-link
                      to="/sponsor-a-show"
                      @click="$route.path !== '/sponsor-a-show' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline"
                      :class="$route.path === '/sponsor-a-show' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:bg-gray-100'"
                      :event="$route.path === '/sponsor-a-show' ? '' : 'click'"
                    >
                      Sponsor a Show
                    </router-link>
                    <router-link
                      to="/past-shows"
                      @click="$route.path !== '/past-shows' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline"
                      :class="$route.path === '/past-shows' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:bg-gray-100'"
                      :event="$route.path === '/past-shows' ? '' : 'click'"
                    >
                      Past Shows
                    </router-link>
                  </div>
                </div>

                <!-- Other Links -->
                <div class="p-3 border-b">
                  <div class="space-y-0">
                    <router-link
                      to="/judgeFinder"
                      @click="$route.path !== '/judgeFinder' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline"
                      :class="$route.path === '/judgeFinder' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:bg-gray-100'"
                      :event="$route.path === '/judgeFinder' ? '' : 'click'"
                    >
                      Judges
                    </router-link>
                    <a
                      href="/learn.html#/"
                      class="block py-2 px-4 rounded-lg no-underline text-gray-700 hover:bg-gray-100"
                      @click="navOpen = false"
                    >
                      Learn
                    </a>
                    <router-link
                      to="/show-series"
                      @click="$route.path !== '/show-series' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline"
                      :class="$route.path === '/show-series' ? 'text-orange-600 bg-orange-50 cursor-default' : 'text-gray-700 hover:bg-gray-100'"
                      :event="$route.path === '/show-series' ? '' : 'click'"
                    >
                      Show Series
                    </router-link>

                    <router-link
                      to="/new-show-signup"
                      @click="$route.path !== '/new-show-signup' ? navOpen = false : null"
                      class="block py-2 px-4 rounded-lg no-underline font-medium"
                      :class="$route.path === '/new-show-signup' ? 'text-orange-600 bg-orange-100 cursor-default' : 'text-orange-600 hover:bg-orange-50'"
                      :event="$route.path === '/new-show-signup' ? '' : 'click'"
                    >
                      Create a show
                    </router-link>
                  </div>
                </div>

                <!-- User Section -->
                <div v-if="currentUser" class="p-3">
                  <div class="flex items-center mb-3">
                    <!-- Circular Avatar with Initial -->
                    <div class="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold text-base mr-3">
                      {{ getUserInitial() }}
                    </div>
                    <h3 class="text-lg font-semibold text-gray-900">
                      {{ currentUser.farm_name || `${currentUser.first_name} ${currentUser.last_name}` }}
                    </h3>
                  </div>
                  <div class="space-y-0">
                    <!-- Upcoming Shows -->
                    <div v-if="upcomingShowLinks.length" class="mb-2">
                      <h4 class="text-sm font-medium text-gray-500 mb-2">Upcoming Shows</h4>
                      <template v-for="(e,i) in upcomingShowLinks">
                        <router-link :key="'upcoming-' + i" :to="`/${e.event_identifier}`" @click="navOpen = false" class="flex items-center justify-between py-1 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-lg no-underline">
                          {{e.name}} <span class="text-xs font-bold whitespace-nowrap">{{formatShowDate(e.start_date)}}</span>
                        </router-link>
                      </template>
                    </div>

                    <!-- Admin Links -->
                    <div v-if="userData.admin_data.adminFor.length" class="mb-2">
                      <h4 class="text-sm font-medium text-gray-500 mb-2">Recent Admin Links</h4>
                      <template v-for="(e,i) in adminDropdownLinks">
                        <router-link  :key="i" :to="{name:'admin.event', params:{show:e.event_identifier}}" @click="navOpen = false" class="block py-1 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-lg no-underline">
                          {{e.name}} <span class="text-xs font-bold">({{e.year}})</span>
                        </router-link>
                      </template>
                    </div>

                    <!-- Admin Dashboard -->
                    <router-link v-if="currentUser.role==='ADMIN'" :to="{name:'Admin'}" @click="navOpen = false" class="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded-lg no-underline">
                      Showman Admin
                    </router-link>

                    <!-- Profile & Signout -->
                    <router-link :to="{name:'my-profile'}" @click="navOpen = false" class="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded-lg no-underline">
                      My Profile
                    </router-link>
                    <button @click="() => {navOpen = false; signout()}" class="w-full text-left py-2 px-4 text-red-600 hover:bg-red-50 rounded-lg">
                      Sign out
                    </button>
                  </div>
                </div>

                <!-- Sign In (when not logged in) -->
                <div v-else class="p-4">
                  <router-link to="/signin" @click="navOpen = false" class="block py-3 px-4 text-center bg-orange-600 text-white hover:bg-orange-700 rounded-lg no-underline font-medium">
                    Sign In
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div :class="{'pb-20_': !$route.meta.hideFooter}" :style="!$route.meta.hideHeader ? { marginTop: (navbarHeight + ($store.getters.isImpersonating ? 40 : 0)) + 'px' } : {}">
        <div class="mx-auto transition-all duration-200 ease-out" >
          <router-view class="min-h-screen"/>
        </div>

        <!---<div v-if="!$route.meta.hideHelp && showHelp" id="help" class="print:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 z-100">
          <div class="mx-auto max-w-7xl px-3 py-2.5 sm:px-6 lg:px-8 flex items-center">
            <p class="text-center md:text-base mr-4 md:mr-0 w-full">
              Having technical trouble? <a href="/shows/#/help" class="font-bold text-showman hover:text-orange-500 no-underline hover:underline">Click here for help.</a> Or send an email to <span class="font-bold">Support@showman.app</span>
            </p>
            <div class="absolute inset-y-0 right-0 flex items-start pr-1 pt-1 sm:items-start sm:pr-2 sm:pt-1">
              <button type="button" class="flex rounded-md p-2 hover:bg-gray-200 focus:outline-none" @click="showHelp = false">
                <span class="sr-only">Dismiss</span>
                <svg class="h-6 w-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>-->

      </div>
      <Footer v-if="!$route.meta.hideFooter" id="footer" class="bg-white"></Footer>
    </template>
    <template v-else>
      <div class="flex min-h-screen items-center justify-center text-center">
        <div class="flex-row py-8 px-4 my-2 border border-gray-400 bg-white rounded-b md:px-24">
          <h1>Showman</h1>
          <h3 class="mb-4">Livestock shows made easy</h3>
          <vue-simple-spinner size="huge" :speed="1.1" :line-size="7" message="Loading" line-fg-color="orange"/>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

import firebaseInit from '@/firebaseInit.js'
import Footer from '@/pages/marketing/Footer.vue'
import ShowSearchBar from '@/components/ShowSearchBar.vue'
import {CHOOSE_PASSWORD_PATH, choosePasswordRedirect, passwordSession} from '@/utils/passwordChangeRequired'

var auth = firebaseInit.auth

export default {
  components: {
    Footer,
    ShowSearchBar
  },
  data () {
    return {
      navOpen: false,
      showHelp: true,
      navbarHeight: 70 // Initial estimated height to prevent shift, will be updated with actual height
    }
  },
  methods: {
    loadAppData () {
      this.$store.dispatch('loadAppData')
    },
    signout () {
      this.$store.dispatch('logUserOut')
        .then(() => {
          // Send logout message to Judge Finder iframe if on judgefinder route
          if (this.$route.path === '/judgefinder') {
            this.sendMessageToJudgeFinderIframe({type: 'PARENT_LOGOUT'})
          }
          if (this.$route.path !== '/') {
            this.$router.replace('/')
          }
        })
    },
    sendMessageToJudgeFinderIframe (message) {
      const iframe = document.querySelector('.judgefinder-iframe')
      if (iframe && iframe.contentWindow) {
        // I updated to get the origin fro the iframe source
        // JOSH 11/11
        iframe.contentWindow.postMessage(message, new URL(iframe.src).origin)// 'http://localhost:5173')
      }
    },
    calculateNavbarHeight () {
      this.$nextTick(() => {
        const topBar = document.getElementById('top-bar')

        if (topBar) {
          const actualHeight = topBar.offsetHeight
          // Only update if there's a meaningful difference to avoid unnecessary re-renders
          if (Math.abs(this.navbarHeight - actualHeight) > 2) {
            this.navbarHeight = actualHeight
          }
        } else {
          // If navbar not found, try again after a short delay
          setTimeout(() => {
            const retryTopBar = document.getElementById('top-bar')
            if (retryTopBar) {
              const actualHeight = retryTopBar.offsetHeight
              if (Math.abs(this.navbarHeight - actualHeight) > 2) {
                this.navbarHeight = actualHeight
              }
            }
          }, 200)
        }
      })
    },
    handleResize () {
      this.calculateNavbarHeight()
    },
    handleShowSelect (selectedItem) {
      // Navigate to the show detail page or series page
      if (selectedItem && selectedItem.event_identifier) {
        // It's a show
        this.$router.push(`/${selectedItem.event_identifier}`)
      } else if (selectedItem && (selectedItem.series_identifier || selectedItem.show_series_id)) {
        // It's a series - check for series_identifier first, then fall back to show_series_id for routing
        const seriesId = selectedItem.series_identifier || selectedItem.show_series_id
        this.$router.push(`/show-series/${seriesId}`)
      }
    },
    formatShowDate (date) {
      if (!date) {
        return ''
      }
      const country = this.currentUser && this.currentUser.address ? this.currentUser.address.country : 'US'
      return this.Localized.formatDate(date, country)
    },
    getUserInitial () {
      if (this.currentUser && this.currentUser.farm_name) {
        return this.currentUser.farm_name.charAt(0).toUpperCase()
      } else if (this.currentUser && this.currentUser.first_name) {
        return this.currentUser.first_name.charAt(0).toUpperCase()
      } else {
        return 'U' // Default initial for unknown users
      }
    },
    toggleMobileMenu () {
      // Close any open filter dropdowns
      this.$root.$emit('close-filter-dropdowns')
      this.navOpen = !this.navOpen
    },
    stopImpersonation () {
      this.$store.dispatch('stopUserImpersonation')
    }
  },
  mounted () {
    this.calculateNavbarHeight()
    window.addEventListener('resize', this.handleResize)

    // Listen for navigation messages from judge-finder iframe
    window.addEventListener('message', (event) => {
      if (event.data.type === 'NAVIGATE' && event.data.path) {
        this.$router.push(event.data.path)
      }
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  created () {
    // Monitor authorization state on the user
    auth.onAuthStateChanged((user) => {
      this.$store.dispatch('authChange', {user: user})
    })

    this.loadAppData()
  },
  computed: {
    currentUser () {
      return this.$store.state.user
    },
    // Where the current page should go instead, once the user's row says a temporary
    // password is waiting; the row usually arrives after the page did (it loads once
    // Firebase restores the session), and this follows both the route and the store.
    choosePasswordLocation () {
      return choosePasswordRedirect(this.$route, passwordSession(this.$store.state, localStorage))
    },
    showMobile () {
      return this.navOpen ? '' : 'hidden'
    },
    userData () {
      return this.$store.state.userData
    },
    adminDropdownLinks () {
      return [...this.userData.admin_data.adminFor].reverse().slice(0, 7)
    },
    upcomingShowLinks () {
      return this.$store.getters.upcomingEnteredShows.slice(0, 7)
    },
    allEvents () {
      const upcomingEvents = this.$store.state.upcomingEvents || []
      const recentEvents = this.$store.state.recentEvents || []

      // Create a Set to track unique event IDs to prevent duplicates
      const seenIds = new Set()
      const uniqueEvents = []

      // Add upcoming events first
      for (const event of upcomingEvents) {
        if (event.event_id && !seenIds.has(event.event_id)) {
          seenIds.add(event.event_id)
          uniqueEvents.push(event)
        }
      }

      // Add recent events, skipping any duplicates
      for (const event of recentEvents) {
        if (event.event_id && !seenIds.has(event.event_id)) {
          seenIds.add(event.event_id)
          uniqueEvents.push(event)
        }
      }

      return uniqueEvents
    },
    allSeries () {
      return this.$store.state.showSeries.filter(s => s.status === 'PUBLISHED' || s.status === 'ARCHIVED') || []
    }
  },
  watch: {
    // Only ever toward the choose screen: leaving it is that page's job.
    choosePasswordLocation (location) {
      if (location && location.path === CHOOSE_PASSWORD_PATH) this.$router.replace(location).catch(() => {})
    },
    '$route' (to, from) {
      document.title = to.meta.title || 'Showman | Livestock Show Management'
      // Close mobile menu on route change
      this.navOpen = false
    },
    async currentUser (newUser, oldUser) {
      // Only send auth messages to Judge Finder iframe when on judgefinder route
      if (this.$route.path !== '/judgefinder') return

      // Send auth state changes to Judge Finder iframe
      if (newUser && !oldUser) {
        // User signed in - get Firebase token and send complete auth data
        try {
          const token = await auth.currentUser?.getIdToken(true)
          this.sendMessageToJudgeFinderIframe({
            type: 'PARENT_SIGNIN',
            token: token,
            user: {
              uid: newUser.uid,
              email: newUser.email,
              firstName: newUser.first_name,
              lastName: newUser.last_name,
              farmName: newUser.farm_name,
              role: newUser.role
            }
          })
        } catch (error) {
          console.error('Error getting Firebase token:', error)
          this.sendMessageToJudgeFinderIframe({type: 'PARENT_SIGNIN', user: newUser})
        }
      } else if (!newUser && oldUser) {
        // User signed out
        this.sendMessageToJudgeFinderIframe({type: 'PARENT_LOGOUT'})
      }
    },
    // Watch for when the app is fully initialized and navbar is rendered
    '$store.state.appInitiated': {
      handler (newVal) {
        if (newVal) {
          // Calculate immediately when app is initiated
          this.calculateNavbarHeight()
          // Also add a small delay as backup to ensure DOM is fully rendered
          setTimeout(() => {
            this.calculateNavbarHeight()
          }, 50)
        }
      },
      immediate: true
    }
  }
}
</script>

<style>
/*body {
  background-color: #F5F7FA;
}*/

#nav {
  padding: 30px;
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  text-align: center;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

/* Used to override notie styles */
.notie-container {
  z-index: 2000;
}

/* Mobile menu transitions using Vue transition classes */
.mobile-menu-enter-active, .mobile-menu-leave-active {
  transition: transform 0.3s ease-in-out;
}

.mobile-menu-enter, .mobile-menu-leave-to {
  transform: translateX(100%);
}

.mobile-menu-enter-to, .mobile-menu-leave {
  transform: translateX(0);
}
</style>
